import React, { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History } from "lucide-react";

/**
 * A button that opens a dialog showing audit log entries for a given entity.
 */
export default function HistoryButton({ entityType, entityId, testId }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit-logs?entity_type=${entityType}&entity_id=${entityId}`);
      setLogs(data);
    } finally { setLoading(false); }
  };

  const openDialog = async () => { setOpen(true); await load(); };

  const actionColor = (a) => ({
    create: "border-[#16A34A] text-[#16A34A]",
    update: "border-[#FFD700] text-[#FFD700]",
    delete: "border-[#DC2626] text-[#DC2626]",
    approved: "border-[#16A34A] text-[#16A34A]",
    rejected: "border-[#DC2626] text-[#DC2626]",
  })[a] || "border-neutral-600 text-neutral-400";

  const renderVal = (v) => {
    if (v == null || v === "") return <span className="text-neutral-600">—</span>;
    if (typeof v === "object") return <code className="text-xs">{JSON.stringify(v)}</code>;
    return String(v);
  };

  return (
    <>
      <button
        onClick={openDialog}
        data-testid={testId || `history-btn-${entityId}`}
        className="text-neutral-500 hover:text-[#FFD700]"
        title="Riwayat perubahan"
      >
        <History size={14} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">Riwayat Perubahan</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="font-mono text-sm text-neutral-500">Memuat...</div>
          ) : logs.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Tidak ada riwayat</div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="border border-neutral-800 bg-[#141414] p-4" data-testid={`audit-entry-${log.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${actionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="font-mono text-sm">{log.user_name}</span>
                    </div>
                    <span className="font-mono text-xs text-neutral-500">
                      {new Date(log.timestamp).toLocaleString("id-ID")}
                    </span>
                  </div>
                  {log.summary && (
                    <div className="text-xs text-neutral-400 mb-2">{log.summary}</div>
                  )}
                  {log.changes && log.changes.length > 0 && (
                    <table className="w-full text-xs font-mono border-collapse mt-2">
                      <thead>
                        <tr className="text-neutral-500 uppercase text-[10px]">
                          <th className="text-left pb-1">Field</th>
                          <th className="text-left pb-1">Sebelum</th>
                          <th className="text-left pb-1">Sesudah</th>
                        </tr>
                      </thead>
                      <tbody>
                        {log.changes.map((c, i) => (
                          <tr key={i} className="border-t border-neutral-800">
                            <td className="py-1 pr-2">{c.field}</td>
                            <td className="py-1 pr-2 text-[#DC2626]/80 line-through">{renderVal(c.before)}</td>
                            <td className="py-1 text-[#16A34A]">{renderVal(c.after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
